import { useNavigationParams } from '../navigation/history'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { RecordContents } from '../RecordTable'
import type { MatrixRow } from '../fund/fundArea'
import type { DocumentRef } from '../../lib/documentReader'
import { formatOfficerDate } from '../../lib/officerFormat'
import type { EditResult, EditTarget } from './contract'
import { resultLabel, valueText } from './contract'
import RecordEditForm from './RecordEditForm'

export default function PendingRecordEdits({
  edits,
  table,
  matrix,
  current,
  currentHref,
  onRecordChanged,
}: {
  edits: readonly EditResult[]
  table: RecordContents
  matrix: MatrixRow[]
  current: boolean
  currentHref: string
  onRecordChanged: () => void
}) {
  const [params, setParams] = useNavigationParams()
  const [selectionGeneration, setSelectionGeneration] = useState(0)
  const [selected, setSelected] = useState<EditResult | null>(
    () => edits.find((edit) => edit.id === params.get('pending_edit')) ?? null
  )
  const requestedId = params.get('pending_edit')
  const [knownRequestedId, setKnownRequestedId] = useState(requestedId)
  const [reportedId, setReportedId] = useState(requestedId)
  // A different scoped bookmark must never leave the former edit actionable.
  // IDs returned by this review do not change the bookmark until navigation.
  if (knownRequestedId !== requestedId) {
    setKnownRequestedId(requestedId)
    if (requestedId !== reportedId) {
      setReportedId(requestedId)
      setSelected(edits.find((edit) => edit.id === requestedId) ?? null)
      setSelectionGeneration((generation) => generation + 1)
    }
  }
  const rows = {
    entity: table.entities,
    instrument: table.instruments,
    position: table.positions,
    trade: table.trades,
  }
  function target(edit: EditResult): EditTarget {
    const row = rows[edit.target_type].find((row) => row.id === edit.target_id) as unknown as
      Record<string, unknown> | undefined
    const name = row?.name ?? row?.instrument_name ?? row?.transaction_type ?? edit.target_type
    return {
      kind: edit.target_type,
      id: edit.target_id,
      label: `${edit.target_type[0].toUpperCase() + edit.target_type.slice(1)} · ${String(name)}`,
    }
  }
  function open(edit: EditResult | null) {
    setReportedId(edit?.id ?? null)
    setSelectionGeneration((generation) => generation + 1)
    setSelected(edit)
    setParams(
      (old) => {
        const next = new URLSearchParams(old)
        if (edit) next.set('pending_edit', edit.id)
        else next.delete('pending_edit')
        return next
      },
      { replace: false }
    )
  }
  function received(edit: EditResult) {
    setReportedId(edit.id)
    setSelected(edit)
    setParams(
      (old) => {
        const next = new URLSearchParams(old)
        next.set('pending_edit', edit.id)
        return next
      },
      { replace: true }
    )
  }
  if (!edits.length && !selected) return null
  const documents: DocumentRef[] = selected
    ? matrix
        .filter(
          (row) =>
            row.object_type === selected.target_type &&
            row.object_id === selected.target_id &&
            row.document_id
        )
        .map((row) => ({
          document_id: row.document_id!,
          display_title: row.document_name,
          page: row.page,
        }))
    : []
  return (
    <section className="record-edit-pending" aria-label="Pending Record changes">
      <h3>Pending Record changes</h3>
      {!current ? (
        <p>
          These changes belong to the current Record.{' '}
          <Link to={currentHref}>Review current changes</Link>
        </p>
      ) : null}
      {edits.map((edit) => (
        <div key={edit.id} className="record-edit-pending-item">
          <b>{target(edit).label}</b>
          {edit.fields
            .filter((field) => field.status === 'pending')
            .map((field) => (
              <span key={field.id}>
                {field.application_scope === 'selected_reference' ? (
                  <>
                    Selected source balance date:{' '}
                    {formatOfficerDate(edit.date_plan?.selected_before?.financial_date) ??
                      'Date not recorded'}
                    {' → '}
                    {formatOfficerDate(valueText(field.proposed_value)) ?? 'Date not recorded'}
                    {' · Current Record: '}
                    {field.record_available
                      ? (formatOfficerDate(valueText(field.approved_value)) ?? 'Date not recorded')
                      : 'Unavailable'}
                  </>
                ) : (
                  <>
                    {field.field.replace(/_/g, ' ')}:{' '}
                    {field.record_available
                      ? valueText(field.approved_value) || 'Not recorded'
                      : 'Unavailable'}{' '}
                    → {valueText(field.proposed_value) || 'Not recorded'}
                  </>
                )}
                {' · '}
                {field.binding_stale
                  ? 'Record changed · review again'
                  : field.application_scope === 'selected_reference' &&
                      edit.date_plan &&
                      !edit.date_plan.can_apply
                    ? 'Pending date review'
                    : resultLabel(field.status)}
              </span>
            ))}
          {current ? (
            <button className="nav-link" onClick={() => open(edit)}>
              Review pending changes
            </button>
          ) : null}
        </div>
      ))}
      {current && selected ? (
        <>
          <button className="nav-link" onClick={() => open(null)}>
            Close change review
          </button>
          <RecordEditForm
            key={selectionGeneration}
            target={target(selected)}
            scope={selected.scope}
            initialEditId={selected.id}
            documents={documents}
            onRecordChanged={onRecordChanged}
            onResult={received}
          />
        </>
      ) : null}
    </section>
  )
}
